import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { Button } from "../Button/Button.js";
import { AuthLayout } from "../AuthLayout/AuthLayout.js";
import { CopyButton } from "../CopyButton/CopyButton.js";
/* empty css              */
import { useEffect, useRef } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/ErrorPage/ErrorPage.tsx
var COPY = {
	generic: {
		icon: "alert-triangle",
		title: "Something went wrong",
		description: "Try again. If it keeps happening, contact support with the reference below."
	},
	notFound: {
		icon: "search",
		title: "Page not found",
		description: "The address may be mistyped, or the page may have moved."
	},
	forbidden: {
		icon: "lock",
		title: "You don’t have access to this page",
		description: "If you need it, ask an administrator to give you access."
	}
};
/**
* A full-page error: an icon, a title that says what happened, one sentence on what to
* do, and a way out. Inside AppShell when the user is signed in, else in AuthLayout's
* frame. Its title is the page's h1, and takes focus when the page appears.
*/
function ErrorPage({ variant = "generic", title, description, actions, reference, homeHref = "/", onRetry = () => window.location.reload(), focusOnMount = true, standalone = false, fill = true, className, ...rest }) {
	const copy = COPY[variant];
	const rootRef = useRef(null);
	useEffect(() => {
		if (!focusOnMount) return;
		const heading = rootRef.current?.querySelector("h1");
		if (!heading) return;
		if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
		heading.focus();
	}, [focusOnMount]);
	const home = /* @__PURE__ */ jsx("a", {
		href: homeHref,
		className: cx("ds-button", variant === "generic" ? "ds-button--secondary" : "ds-button--primary", "ds-button--md", "ds-error-page__home"),
		children: /* @__PURE__ */ jsx("span", {
			className: "ds-button__content",
			children: /* @__PURE__ */ jsx("span", {
				className: "ds-button__face",
				children: "Go to home"
			})
		})
	});
	const defaultActions = variant === "generic" ? /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Button, {
		onClick: onRetry,
		children: "Try again"
	}), home] }) : home;
	const referenceLine = reference && /* @__PURE__ */ jsxs("p", {
		className: "ds-error-page__reference",
		children: [
			/* @__PURE__ */ jsx("span", { children: "Error reference" }),
			" ",
			/* @__PURE__ */ jsx("code", {
				className: "ds-error-page__code",
				children: reference
			}),
			/* @__PURE__ */ jsx(CopyButton, {
				value: reference,
				label: "Copy reference"
			})
		]
	});
	if (standalone) return /* @__PURE__ */ jsx("div", {
		ref: rootRef,
		className: cx("ds-error-page-frame", className),
		children: /* @__PURE__ */ jsxs(AuthLayout, {
			...rest,
			fill,
			title: title ?? copy.title,
			subtitle: description ?? copy.description,
			children: [/* @__PURE__ */ jsx("div", {
				className: "ds-error-page__actions ds-error-page__actions--standalone",
				children: actions ?? defaultActions
			}), referenceLine]
		})
	});
	return /* @__PURE__ */ jsxs("div", {
		...rest,
		ref: rootRef,
		className: cx("ds-error-page", `ds-error-page--${variant}`, className),
		children: [
			/* @__PURE__ */ jsx("span", {
				className: "ds-error-page__icon",
				"aria-hidden": "true",
				children: /* @__PURE__ */ jsx(Icon, { name: copy.icon })
			}),
			/* @__PURE__ */ jsx("h1", {
				tabIndex: -1,
				className: "ds-error-page__title",
				children: title ?? copy.title
			}),
			/* @__PURE__ */ jsx("p", {
				className: "ds-error-page__description",
				children: description ?? copy.description
			}),
			/* @__PURE__ */ jsx("div", {
				className: "ds-error-page__actions",
				children: actions ?? defaultActions
			}),
			referenceLine
		]
	});
}
//#endregion
export { ErrorPage };

//# sourceMappingURL=ErrorPage.js.map